// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { isNativePlatform, platformReady } from '@/lib/platform';
import { setWriteTelemetry } from '@/lib/debug/authTelemetry';
import { beginWrite, endWrite } from '@/lib/auth/pendingWritesGate';

const SUPABASE_URL = "https://xhoqjcdsprhrrvbminxe.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inhob3FqY2RzcHJocnJ2Ym1pbnhlIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTYzODE1MjIsImV4cCI6MjA3MTk1NzUyMn0.fMM2en2wr2V4DBSeoEuwJZwElBZnr8GaYDvR2-ldKWE";

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

// Storage strategy:
// - Web: sessionStorage (per-tab isolation).
// - Native (Capacitor Android/iOS): @capacitor/preferences ONLY. Writes to
//   Android SharedPreferences / iOS UserDefaults, which survive force-close.
//
// CRITICAL: on native the setItem path does NOT fall back to WebView
// localStorage on failure. WebView storage is not durable across force-close
// on Android, so silently landing there defeats the entire purpose of
// having a native storage adapter. If Preferences fails, we retry once and
// then log CRITICAL — the user's next force-close would log them out and we
// want that noise in the logcat, not a silent regression.
//
// Every storage call awaits `platformReady` first so a supabase-js write
// that fires immediately on cold boot cannot race the Capacitor bridge and
// end up taking the web branch on what is really a native device.
type MaybePromise<T> = T | Promise<T>;
interface AsyncStorageAdapter {
  getItem: (key: string) => MaybePromise<string | null>;
  setItem: (key: string, value: string) => MaybePromise<void>;
  removeItem: (key: string) => MaybePromise<void>;
}

let _preferencesModulePromise: Promise<typeof import('@capacitor/preferences')> | null = null;
const loadPreferences = () => {
  if (!_preferencesModulePromise) {
    _preferencesModulePromise = import('@capacitor/preferences');
  }
  return _preferencesModulePromise;
};

const hybridStorage: AsyncStorageAdapter = {
  async getItem(key: string): Promise<string | null> {
    await platformReady;
    if (isNativePlatform()) {
      try {
        const { Preferences } = await loadPreferences();
        const { value } = await Preferences.get({ key });
        if (value !== null && value !== undefined) return value;
        // One-time additive migration: if Preferences is empty but WebView
        // localStorage still holds a legacy session, adopt it and mirror
        // into Preferences so future cold boots restore correctly.
        try {
          const legacy = window.localStorage.getItem(key);
          if (legacy !== null) {
            await Preferences.set({ key, value: legacy });
            return legacy;
          }
        } catch { /* localStorage may be unavailable */ }
        return null;
      } catch (e) {
        // Do NOT fall back to WebView storage here — a stale localStorage
        // read could silently "restore" a wiped session and mask the real
        // Preferences failure. Return null and log loudly.
        console.error('[auth-storage] CRITICAL: Preferences.get failed on native — session will not restore:', e);
        return null;
      }
    }
    try { return window.sessionStorage.getItem(key); } catch { return null; }
  },
  async setItem(key: string, value: string): Promise<void> {
    beginWrite();
    try {
      await platformReady;
      if (isNativePlatform()) {
        let Preferences: typeof import('@capacitor/preferences').Preferences | null = null;
        try {
          Preferences = (await loadPreferences()).Preferences;
        } catch (e) {
          console.error('[auth-storage] CRITICAL: failed to load @capacitor/preferences on native — session will NOT persist across force-close:', e);
          setWriteTelemetry({ key, status: 'fail', error: 'preferences module load failed', at: Date.now() });
          return;
        }
        try {
          await Preferences.set({ key, value });
          setWriteTelemetry({ key, status: 'success', at: Date.now() });
          return;
        } catch (e1) {
          console.warn('[auth-storage] Preferences.set failed, retrying once:', e1);
          try {
            await Preferences.set({ key, value });
            setWriteTelemetry({ key, status: 'retry-success', at: Date.now() });
            return;
          } catch (e2) {
            // Deliberate: no localStorage fallback. WebView storage dies on
            // force-close, so silently landing there would create the exact
            // bug we're fixing. Loud error → visible in logcat.
            console.error('[auth-storage] CRITICAL: Preferences.set failed twice — session NOT persisted durably. Force-close will log out user.', e2);
            setWriteTelemetry({ key, status: 'fail', error: String((e2 as Error)?.message ?? e2), at: Date.now() });
            return;
          }
        }
      }
      try { window.sessionStorage.setItem(key, value); } catch { /* ignore */ }
    } finally {
      endWrite();
    }
  },
  async removeItem(key: string): Promise<void> {
    await platformReady;
    if (isNativePlatform()) {
      try {
        const { Preferences } = await loadPreferences();
        await Preferences.remove({ key });
      } catch (e) {
        console.error('[auth-storage] Preferences.remove failed:', e);
      }
      // Also clear any legacy localStorage copy so a stale session can't
      // resurrect on the next migration pass.
      try { window.localStorage.removeItem(key); } catch { /* ignore */ }
      return;
    }
    try { window.sessionStorage.removeItem(key); } catch { /* ignore */ }
  },
};

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: hybridStorage as unknown as Storage,
    persistSession: true,
    autoRefreshToken: true,
  }
});
